/**
 * SoundService - Service for playing notification sounds
 * Uses Web Audio API to generate different types of notification sounds
 */

type NotificationType = 'success' | 'info' | 'warning' | 'error' | 'completion'
type OscillatorType = 'sine' | 'square' | 'sawtooth' | 'triangle'

class SoundService {
  private audioContext: AudioContext | null = null
  private enabled = true
  private volume = 0.3 // Default volume (0.0 to 1.0)

  /**
   * Initialize audio context (lazy initialization)
   */
  private getAudioContext(): AudioContext {
    if (!this.audioContext) {
      const AudioContextClass =
        window.AudioContext || (window as any).webkitAudioContext
      this.audioContext = new AudioContextClass()
    }
    return this.audioContext
  }

  /**
   * Play a beep with specific frequency and duration
   * @param frequency - Frequency in Hz
   * @param duration - Duration in milliseconds
   * @param type - Oscillator type ('sine', 'square', 'sawtooth', 'triangle')
   */
  playBeep(
    frequency: number,
    duration: number,
    type: OscillatorType = 'sine'
  ): void {
    if (!this.enabled) return

    try {
      const context = this.getAudioContext()
      const oscillator = context.createOscillator()
      const gainNode = context.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(context.destination)

      oscillator.frequency.value = frequency
      oscillator.type = type

      gainNode.gain.setValueAtTime(this.volume, context.currentTime)
      gainNode.gain.exponentialRampToValueAtTime(
        0.01,
        context.currentTime + duration / 1000
      )

      oscillator.start(context.currentTime)
      oscillator.stop(context.currentTime + duration / 1000)
    } catch (error) {
      console.error('Error playing sound:', error)
    }
  }

  /**
   * Play success/completion sound
   */
  playSuccess(): void {
    const context = this.getAudioContext()
    const now = context.currentTime

    // Two ascending notes
    this.playTone(523.25, 0.1, now) // C5
    this.playTone(659.25, 0.15, now + 0.1) // E5
  }

  /**
   * Play info sound
   */
  playInfo(): void {
    const context = this.getAudioContext()
    const now = context.currentTime

    // Single gentle tone
    this.playTone(440, 0.2, now) // A4
  }

  /**
   * Play warning sound
   */
  playWarning(): void {
    const context = this.getAudioContext()
    const now = context.currentTime

    // Two identical tones
    this.playTone(440, 0.15, now) // A4
    this.playTone(440, 0.15, now + 0.2) // A4
  }

  /**
   * Play error sound
   */
  playError(): void {
    const context = this.getAudioContext()
    const now = context.currentTime

    // Descending tones
    this.playTone(329.63, 0.15, now, 'square') // E4
    this.playTone(293.66, 0.15, now + 0.15, 'square') // D4
    this.playTone(261.63, 0.2, now + 0.3, 'square') // C4
  }

  /**
   * Play completion sound (for long operations)
   */
  playCompletion(): void {
    const context = this.getAudioContext()
    const now = context.currentTime

    // Ascending arpeggio
    this.playTone(523.25, 0.1, now) // C5
    this.playTone(659.25, 0.1, now + 0.1) // E5
    this.playTone(783.99, 0.15, now + 0.2) // G5
  }

  /**
   * Helper method to play a single tone
   * @param frequency - Frequency in Hz
   * @param duration - Duration in seconds
   * @param startTime - When to start the tone
   * @param type - Oscillator type
   */
  playTone(
    frequency: number,
    duration: number,
    startTime: number,
    type: OscillatorType = 'sine'
  ): void {
    if (!this.enabled) return

    try {
      const context = this.getAudioContext()
      const oscillator = context.createOscillator()
      const gainNode = context.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(context.destination)

      oscillator.frequency.value = frequency
      oscillator.type = type

      // Envelope for smoother sound
      gainNode.gain.setValueAtTime(0, startTime)
      gainNode.gain.linearRampToValueAtTime(this.volume, startTime + 0.01)
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration)

      oscillator.start(startTime)
      oscillator.stop(startTime + duration)
    } catch (error) {
      console.error('Error playing tone:', error)
    }
  }

  /**
   * Play sound based on notification type
   * @param type - Notification type ('success', 'info', 'warning', 'error', 'completion')
   */
  play(type: NotificationType): void {
    switch (type) {
      case 'success':
        this.playSuccess()
        break
      case 'info':
        this.playInfo()
        break
      case 'warning':
        this.playWarning()
        break
      case 'error':
        this.playError()
        break
      case 'completion':
        this.playCompletion()
        break
      default:
        this.playInfo()
    }
  }

  /**
   * Enable/disable sounds
   * @param enabled
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled
  }

  /**
   * Set volume
   * @param volume - Volume level (0.0 to 1.0)
   */
  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume))
  }

  /**
   * Check if sounds are enabled
   */
  isEnabled(): boolean {
    return this.enabled
  }
}

// Export singleton instance
export default new SoundService()
